import type { CSSProperties } from "react";
import { useLocation, useMatches } from "@tanstack/react-router";
import { creatorInitials } from "../../../shared/src/creator-initials";
import type { SiteShell } from "../server/shell";
import { BagLink } from "./bag-link";
import { CurrencyPicker } from "./currency-picker";
import { MenuSheet } from "./menu-sheet";
import { SocialLinks } from "./social-links";
import { NavigationMenu, NavigationDisclosureLinks } from "./ui-kit/navigation-menu";
import { HeaderLayout } from "./header-layout";

/** A path on this site keeps an owner's preview; every other address is left as written. */
export function sitePath(shell: SiteShell, path: string) {
  if (path === "/bible" && shell.bibleEntryPath) path = shell.bibleEntryPath;
  return shell.preview && path.startsWith("/")
    ? `${path}${path.includes("?") ? "&" : "?"}preview=1`
    : path;
}

function current(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(`${href}/`) ? "page" : undefined;
}

/** A search button: the shop's listing or the posts, whose own search field takes it from there. */
function HeaderSearch({ shell, path }: { shell: SiteShell; path: string }) {
  return (
    <a
      className="header-icon header-search"
      href={sitePath(shell, path)}
      aria-label={path.split("?")[0] === "/merch" ? "Search the shop" : "Search posts"}
    >
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="11" cy="11" r="6.25" />
        <path d="m20 20-4.5-4.5" />
      </svg>
    </a>
  );
}

/**
 * The header's picture or looping video under its tint. React leaves `muted` out of server HTML,
 * and browsers only start a muted video by themselves, so the video is written as markup.
 */
function HeaderBackdrop({
  background,
}: {
  background: NonNullable<SiteShell["header"]["background"]>;
}) {
  const attribute = (value: string) =>
    value.replaceAll("&", "&amp;").replaceAll('"', "&quot;").replaceAll("<", "&lt;");
  const style = {
    "--header-backdrop-overlay": String(background.overlay),
    "--header-backdrop-focus": background.focus,
    ...(background.overlayColor ? { "--header-backdrop-tint": background.overlayColor } : {}),
  } as CSSProperties;
  const copies = background.imageSrcset
    ? ` srcset="${attribute(background.imageSrcset)}" sizes="100vw"`
    : "";
  const picture = background.image
    ? // No priority of its own: the page's lead picture is preloaded first, and a picture on
      // screen still loads at once.
      `<img src="${attribute(background.image)}"${copies} alt="" decoding="async">`
    : "";
  const video = background.video
    ? `<video src="${attribute(background.video)}"${background.image ? ` poster="${attribute(background.image)}"` : ""} autoplay muted loop playsinline disablepictureinpicture preload="auto" tabindex="-1"></video>`
    : "";
  return (
    <div
      className="site-header-backdrop"
      aria-hidden="true"
      style={style}
      dangerouslySetInnerHTML={{ __html: picture + video }}
    />
  );
}

/** The creator's header: brand, menu, bag and account action. */
export function SiteHeader({ shell }: { shell: SiteShell }) {
  const { pathname } = useLocation();
  // Home, at its address or drawn by the editor's preview.
  const atHome = useMatches({
    select: (matches) =>
      matches.some(
        (match) =>
          (match.loaderData as { page?: { path?: string } } | undefined)?.page?.path === "/",
      ),
  });
  const { creator, header, nav } = shell;
  const signUpHref = "/sign-up?returnTo=%2Faccount";
  // The owner, signed in on their own site, gets the owner bar (or their dashboard's Website
  // view) instead of an account action.
  const accountAction = !shell.owner && !shell.ownerWebsiteView;
  return (
    <header
      className="site-header"
      data-brand-name={header.showName ? undefined : "hidden"}
      // On a phone: the menu button last, with the name at the left.
      data-mobile-menu={header.mobileMenu === "end" ? "end" : undefined}
      data-brand-color={header.brandColor}
      data-navigation-font={header.navigationFont}
      data-navigation-style={header.navigationStyle}
      data-navigation-color={header.navigationColor}
      data-navigation-case={header.navigationCase}
      style={
        header.navigationTracking || header.navigationSize
          ? ({
              ...(header.navigationTracking
                ? { "--navigation-tracking": `${header.navigationTracking / 100}em` }
                : {}),
              ...(header.navigationSize
                ? { "--navigation-size": `${header.navigationSize}px` }
                : {}),
            } as CSSProperties)
          : undefined
      }
      // Over Home's first section, as a storefront theme can set it.
      data-overlay={header.overlay && (pathname === "/" || atHome) ? "" : undefined}
      // Without the brand on Home, whose link-in-bio profile shows the photo and name already.
      data-brand-home={!header.brandOnHome && (pathname === "/" || atHome) ? "hidden" : undefined}
      data-backdrop={header.background ? "" : undefined}
      data-tone={header.tone}
    >
      {header.background && <HeaderBackdrop background={header.background} />}
      <HeaderLayout menu={header.menu}>
        {header.socials && header.style === "centered" && (
          <SocialLinks socials={creator.socials} className="header-socials" />
        )}
        <div className="brand-slot">
          <a
            className="brand"
            href={sitePath(shell, header.homePath)}
            aria-label="Homepage"
            // The brand is Home's link, so on Home it's the current page, as a menu link would be.
            aria-current={
              (header.homePath === "/" ? pathname === "/" || atHome : pathname === header.homePath)
                ? "page"
                : undefined
            }
          >
            {creator.logoUrl ? (
              // Low priority: React preloads every other eager picture ahead of the page's own,
              // and this 32-pixel logo's original competed with the page's largest picture.
              <img
                src={creator.logoUrl}
                srcSet={creator.logoSrcset ?? undefined}
                sizes={creator.logoSrcset ? "32px" : undefined}
                alt=""
                width="32"
                height="32"
                fetchPriority="low"
                decoding="async"
              />
            ) : header.showInitials !== false ? (
              <span className="brand-initials" aria-hidden="true">
                {creatorInitials(header.name)}
              </span>
            ) : null}
            <span className="brand-name">{header.name}</span>
            {header.tagline && <span className="brand-tagline">{header.tagline}</span>}
          </a>
        </div>
        {nav.some((item) => item.group) ? (
          <NavigationMenu
            id="creator-navigation"
            currentPath={pathname}
            style={header.navigationStyle}
            items={nav.map((item) => ({
              ...item,
              href: sitePath(shell, item.href),
              current: current(pathname, item.href) === "page",
              image:
                item.group && item.href === "/library"
                  ? (creator.coverUrl ?? undefined)
                  : undefined,
            }))}
          />
        ) : (
          <nav className="desktop-nav" aria-label="Main navigation">
            {nav.map((item) => (
              <a
                key={item.href}
                href={sitePath(shell, item.href)}
                data-nav-path={item.href.startsWith("/") ? item.href : undefined}
                aria-current={current(pathname, item.href)}
              >
                {item.label}
              </a>
            ))}
          </nav>
        )}
        <div className="header-actions">
          {header.socials && header.style !== "centered" && (
            <SocialLinks socials={creator.socials} className="header-socials" />
          )}
          {header.currency && (
            <CurrencyPicker
              creatorId={creator.id}
              currency={header.currency}
              id="header-currency"
            />
          )}
          {header.searchPath && <HeaderSearch shell={shell} path={header.searchPath} />}
          {shell.bag && <BagLink creatorId={creator.id} href={sitePath(shell, "/merch/cart")} />}
          {header.button ? (
            // The creator's own button in place of the account action, such as Get in touch.
            <a
              className="button button-small button-primary"
              href={sitePath(shell, header.button.href)}
            >
              {header.button.label}
            </a>
          ) : !accountAction ? null : header.signUp ? (
            <>
              <a
                className="header-signin"
                href={header.app}
                data-reload=""
                data-creator-auth-link=""
              >
                Sign in
              </a>
              <a
                className="button button-small button-primary"
                href={signUpHref}
                data-reload=""
                data-creator-auth-link=""
              >
                {header.signUpLabel || "Sign up"}
              </a>
            </>
          ) : header.accountIcon ? (
            <a
              className="header-account"
              href={header.app}
              aria-label={header.appLabel.replace(" ↗", "")}
              data-reload=""
              data-creator-auth-link={header.sessionHint ? undefined : ""}
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="8" r="3.75" />
                <path d="M4.75 20c.9-3.6 3.7-5.5 7.25-5.5s6.35 1.9 7.25 5.5" />
              </svg>
            </a>
          ) : (
            <a
              className="button button-small button-outline"
              href={header.app}
              data-reload=""
              data-creator-auth-link={header.sessionHint ? undefined : ""}
            >
              {header.appLabel}
            </a>
          )}
        </div>
        <MenuSheet>
          <NavigationDisclosureLinks
            items={nav.map((item) => ({
              ...item,
              href: sitePath(shell, item.href),
              current: current(pathname, item.href) === "page",
            }))}
          />
          {shell.bag && (
            <BagLink creatorId={creator.id} href={sitePath(shell, "/merch/cart")} menu />
          )}
          {header.searchPath && (
            <a href={sitePath(shell, header.searchPath)}>
              {header.searchPath.split("?")[0] === "/merch" ? "Search the shop" : "Search posts"}
            </a>
          )}
          {(header.socials || header.currency) && (
            <div className="menu-sheet-extras">
              {header.socials && <SocialLinks socials={creator.socials} />}
              {header.currency && (
                <CurrencyPicker
                  creatorId={creator.id}
                  currency={header.currency}
                  id="menu-currency"
                />
              )}
            </div>
          )}
          {header.button ? (
            <a
              className="button menu-sheet-action button-primary"
              href={sitePath(shell, header.button.href)}
            >
              {header.button.label}
            </a>
          ) : !accountAction ? null : (
            <>
              {header.signUp && (
                <a href={header.app} data-reload="" data-creator-auth-link="">
                  Sign in
                </a>
              )}
              <a
                className={`button menu-sheet-action ${header.signUp ? "button-primary" : "button-outline"}`}
                href={header.signUp ? signUpHref : header.app}
                data-reload=""
                data-creator-auth-link={header.sessionHint ? undefined : ""}
              >
                {header.signUp ? header.signUpLabel || "Sign up" : header.appLabel}
              </a>
            </>
          )}
        </MenuSheet>
      </HeaderLayout>
    </header>
  );
}
